// src/pages/CreateEmployee.jsx
import React, { useEffect, useCallback, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import toast from "react-hot-toast";
import { useCreateEmployeeMutation } from "../toolkitServices/employeeApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetCountersQuery } from "../toolkitServices/counterApi";
import { useGetAllRolesQuery } from "../toolkitServices/roleApi";
import { useGetAllDepartmentsQuery } from "../toolkitServices/departmentApi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Select from "react-select";

// ✅ NEW: company fetch for employeeEmailInitial
import { useGetAllCompaniesQuery } from "../toolkitServices/companyApi";
import { indianStates } from "../utils/helpers";

/* ---------- Shared UI tokens ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

/* ---- helpers ---- */
const safe = (v) => (v == null ? "" : String(v));
const sanitizeEmailInitial = (v) =>
  safe(v)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, "");

const CreateEmployee = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);

  const { data: branches } = useGetAllBranchesQuery();
  const { data: counters } = useGetCountersQuery();
  const { data: roles } = useGetAllRolesQuery();
  const { data: departments } = useGetAllDepartmentsQuery();

  // ✅ get company (1 per tenant)
  const { data: companiesRes } = useGetAllCompaniesQuery({
    page: 1,
    limit: 1,
    search: "",
  });

  const companyDoc = companiesRes?.data?.[0] || null;
  const companyEmailInitial = sanitizeEmailInitial(
    companyDoc?.employeeEmailInitial
  );

  const officialEmailSuffix = useMemo(() => {
    // fixed part after @
    return `@${companyEmailInitial || "company"}.com`;
  }, [companyEmailInitial]);

  const [createEmployee, { isLoading: isSaving, isSuccess, data, error }] =
    useCreateEmployeeMutation();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    reset,
    setFocus,
    watch,
    control,
    setValue,
    getFieldState, // ✅ FIX: to detect which field is invalid (no UI change)
  } = useForm({
    mode: "onBlur",
    shouldUnregister: false,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",

      // ✅ NEW: only prefix (before @)
      officialEmailPrefix: "",

      contact: "",
      adhar: "",
      pan: "",
      dob: "",
      gender: "male",
      addressLine1: "",
      city: "",
      pincode: "",
      state: "Maharashtra",
      country: "india",

      // ✅ Branches
      branches: [],
      branch: null,

      // ✅ Counters (multi + default)
      counters: [],
      counter: null,

      department: "",
      role: "",
      salary: "",
      employeeCommission: "",

      bankName: "",
      accountHolderName: "",
      accountNumber: "",
      accountBranch: "",
      ifscCode: "",

      createLogin: false,
      password: "",
    },
  });

  const selectedBranches = watch("branches");
  const defaultBranch = watch("branch");

  const selectedCounters = watch("counters");
  const defaultCounter = watch("counter");

  const createLogin = watch("createLogin");
  const officialEmailPrefix = watch("officialEmailPrefix");

  /* ---------- react-select options ---------- */
  const allBranchOptions = useMemo(() => {
    const list = branches?.data || [];
    return list.map((b) => ({ value: b._id, label: b.branchName }));
  }, [branches]);

  const defaultBranchOptions = useMemo(() => {
    return Array.isArray(selectedBranches) ? selectedBranches : [];
  }, [selectedBranches]);

  const primaryBranchId = useMemo(() => {
    return defaultBranch?.value || selectedBranches?.[0]?.value || "";
  }, [defaultBranch, selectedBranches]);

  const selectedBranchIdSet = useMemo(() => {
    const ids = Array.isArray(selectedBranches)
      ? selectedBranches.map((b) => b?.value).filter(Boolean)
      : [];
    return new Set(ids.map((x) => String(x)));
  }, [selectedBranches]);

  // ✅ counters options (branch filter)
  const counterOptions = useMemo(() => {
    const list = counters?.data || [];
    if (!selectedBranchIdSet || selectedBranchIdSet.size === 0) return [];
    const filtered = list.filter((c) =>
      selectedBranchIdSet.has(String(c.branchId))
    );
    return filtered.map((c) => ({ value: c._id, label: c.counterName }));
  }, [counters, selectedBranchIdSet]);

  const defaultCounterOptions = useMemo(() => {
    return Array.isArray(selectedCounters) ? selectedCounters : [];
  }, [selectedCounters]);

  /* ---------- keep default branch valid ---------- */
  useEffect(() => {
    if (!defaultBranch?.value) return;

    const stillExists = (selectedBranches || []).some(
      (b) => String(b?.value) === String(defaultBranch.value)
    );

    if (!stillExists) {
      setValue("branch", null, { shouldValidate: true, shouldDirty: true });
    }
  }, [selectedBranches, defaultBranch, setValue]);

  /* ---------- keep default counter valid ---------- */
  useEffect(() => {
    if (!defaultCounter?.value) return;

    const stillExists = (selectedCounters || []).some(
      (c) => String(c?.value) === String(defaultCounter.value)
    );

    if (!stillExists) {
      setValue("counter", null, { shouldValidate: true, shouldDirty: true });
    }
  }, [selectedCounters, defaultCounter, setValue]);

  // ✅ branch change => counters reset (recommended)
  useEffect(() => {
    setValue("counters", [], { shouldDirty: true, shouldValidate: true });
    setValue("counter", null, { shouldDirty: true, shouldValidate: true });
  }, [primaryBranchId, setValue]);

  /* ---------- focus by step ---------- */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (step === 1) setFocus("firstName");
      if (step === 2) setFocus("addressLine1");
      if (step === 3) setFocus("department");
      if (step === 4) setFocus("bankName");
    });
    return () => cancelAnimationFrame(id);
  }, [step, setFocus]);

  /* ---------- step wise trigger (avoid hidden fields failing) ---------- */
  const step1Fields = useMemo(
    () => [
      "firstName",
      "lastName",
      "email",
      "contact",
      "adhar",
      "pan",
      "dob",
      "gender",
    ],
    []
  );

  const step2Fields = useMemo(
    () => ["addressLine1", "city", "pincode", "state", "country"],
    []
  );

  const step3Fields = useCallback(
    (loginOn) => [
      "branches",
      "branch",
      "department",

      // ✅ NEW counter fields
      "counters",
      "counter",

      "role",
      "salary",
      "employeeCommission",
      "createLogin",
      ...(loginOn ? ["officialEmailPrefix", "password"] : []),
    ],
    []
  );

  const nextStep = useCallback(async () => {
    const fields =
      step === 1
        ? step1Fields
        : step === 2
        ? step2Fields
        : step === 3
        ? step3Fields(createLogin)
        : [];

    // ✅ FIX: shouldFocus makes it jump to first invalid field (no design change)
    const ok = await trigger(fields, { shouldFocus: true });

    // ✅ FIX: show which field is blocking (toast only; UI unchanged)
    if (!ok) {
      const firstInvalid = fields.find((f) => getFieldState(f).invalid);
      if (firstInvalid) toast.error(`Please check: ${firstInvalid}`);
      return;
    }

    setStep((s) => (s < 4 ? s + 1 : s));
  }, [
    trigger,
    step,
    step1Fields,
    step2Fields,
    step3Fields,
    createLogin,
    getFieldState,
  ]);

  const prevStep = useCallback(() => setStep((s) => (s > 1 ? s - 1 : s)), []);

  /* ---------- keyboard shortcuts ---------- */
  useEffect(() => {
    const handler = (e) => {
      const save = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
      const back = (e.ctrlKey || e.metaKey) && e.key === "Backspace";
      const prev = e.altKey && e.key === "ArrowLeft";

      if (save) {
        e.preventDefault();
        if (step === 4) handleSubmit(onSubmit)();
        else nextStep();
      }
      if (back) {
        e.preventDefault();
        navigate(-1);
      }
      if (prev) {
        e.preventDefault();
        prevStep();
      }
    };

    // ✅ FIX: capture=true so it works even inside react-select input
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [step, handleSubmit, nextStep, prevStep, navigate]);

  /* ---------- API feedback ---------- */
  useEffect(() => {
    if (error) {
      const msg =
        error?.data?.msg ||
        error?.data?.message ||
        error?.error ||
        "Failed to add employee";
      toast.error(msg, { position: "top-center" });
    }
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Employee added successfully!", { position: "top-center" });
      if (data?.data?.credentials) {
        const creds = data.data.credentials;
        toast.success(
          `Login Created!\nEmail: ${creds.email}\nPassword: ${creds.password}`,
          { duration: 6000, position: "top-center" }
        );
      }
      reset();
      setStep(1);
      requestAnimationFrame(() => setFocus("firstName"));
    }
  }, [isSuccess, data, reset, setFocus]);

  /* ---------- submit ---------- */
  const onSubmit = async (form) => {
    const onlyDigits = (v = "") => v.toString().replace(/\D/g, "");

    const selectedBranchIds = Array.isArray(form.branches)
      ? form.branches.map((x) => x?.value).filter(Boolean)
      : [];

    const defaultBranchId = form.branch?.value || "";

    // ✅ counters mapping
    const selectedCounterIds = Array.isArray(form.counters)
      ? form.counters.map((x) => x?.value).filter(Boolean)
      : [];

    const defaultCounterId = form.counter?.value || "";

    const prefix = safe(form.officialEmailPrefix).trim().toLowerCase();
    const officialEmailFinal = form.createLogin
      ? `${prefix}${officialEmailSuffix}`
      : "";

    const payload = {
      firstName: form.firstName?.trim(),
      lastName: form.lastName?.trim() || "",
      email: (form.email || "").trim(),

      // ✅ send final official email
      officialEmail: officialEmailFinal,

      contact: onlyDigits(form.contact || ""),
      adhar: onlyDigits(form.adhar || ""),
      pan: (form.pan || "").trim().toUpperCase(),
      dob: form.dob || "",
      gender: form.gender,
      addressLine1: (form.addressLine1 || "").trim(),
      city: (form.city || "").trim(),
      pincode: onlyDigits(form.pincode || ""),
      state: form.state,
      country: form.country,

      // ✅ branches
      branch: defaultBranchId,
      branches: selectedBranchIds,

      // ✅ counters (multi + default)
      counter: defaultCounterId,
      counters: selectedCounterIds,

      department: form.department,
      role: form.role,
      salary: (form.salary || "").trim(),

      employeeCommission: String(form.employeeCommission ?? "").trim(),

      bankName: (form.bankName || "").trim(),
      accountHolderName: (form.accountHolderName || "").trim(),
      accountNumber: onlyDigits(form.accountNumber || ""),
      accountBranch: (form.accountBranch || "").trim(),
      ifscCode: (form.ifscCode || "").trim().toUpperCase(),

      createLogin: !!form.createLogin,
      password: form.password || "",
    };

    try {
      await createEmployee(payload).unwrap();
    } catch {}
  };

  const loading = isSaving || isSubmitting;

  /* ---------- react-select styles (match inputs) ---------- */
  const rsStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: 42,
        borderRadius: 8,
        boxShadow: "none",
        paddingLeft: 2,
        paddingRight: 2,
        fontSize: 14,
        borderColor: state?.isFocused ? "#9481ce" : "#d1d5db",
        ":hover": {
          borderColor: "#9481ce",
        },
      }),
      valueContainer: (base) => ({
        ...base,
        paddingTop: 4,
        paddingBottom: 4,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  return (
    <div className="px-4 py-2">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border px-2 py-2 flex items-center gap-3">
          <Link
            to="/members/employee-list"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border hover:bg-gray-100"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>
          <div className="flex-1 flex justify-between items-center">
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91]">
                Create Employee
              </h1>
              <p className="text-xs text-gray-500">
                Ctrl+S save • Ctrl+Backspace back • Alt+← prev step
              </p>
            </div>
            <span className="hidden sm:block text-xs text-gray-500 pr-2">
              Step {step} of 4
            </span>
          </div>
        </div>
      </div>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-5xl mx-auto bg-white shadow-md rounded-xl mt-2 border relative"
      >
        {loading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <form className="p-5" onSubmit={(e) => e.preventDefault()}>
          {/* STEP 1 — EMPLOYEE DETAILS */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="First Name" required error={errors.firstName}>
                <input
                  {...register("firstName", {
                    required: "First name is required",
                  })}
                  placeholder="Enter first name"
                  className={`${baseInput} ${
                    errors.firstName ? errRing : okRing
                  }`}
                />
              </Field>

              <Field label="Last Name" required error={errors.lastName}>
                <input
                  {...register("lastName", {
                    required: "Last name is required",
                  })}
                  placeholder="Enter last name"
                  className={`${baseInput} ${
                    errors.lastName ? errRing : okRing
                  }`}
                />
              </Field>

              <Field label="Personal Email" required error={errors.email}>
                <input
                  type="email"
                  {...register("email", {
                    required: "Personal email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email address",
                    },
                  })}
                  placeholder="Enter personal email"
                  className={`${baseInput} ${errors.email ? errRing : okRing}`}
                />
              </Field>

              <Field label="Contact" required error={errors.contact}>
                <input
                  {...register("contact", {
                    required: "Contact is required",
                    validate: (v) =>
                      /^\d{10}$/.test(v || "") || "Must be 10 digits",
                  })}
                  placeholder="10-digit mobile"
                  className={`${baseInput} ${
                    errors.contact ? errRing : okRing
                  }`}
                />
              </Field>

              <Field label="Aadhar" error={errors.adhar}>
                <input
                  {...register("adhar", {
                    validate: (v) =>
                      v === "" || /^\d{12}$/.test(v) || "Must be 12 digits",
                  })}
                  placeholder="12-digit Aadhar (optional)"
                  className={`${baseInput} ${errors.adhar ? errRing : okRing}`}
                />
              </Field>

              <Field label="PAN" error={errors.pan}>
                <input
                  {...register("pan", {
                    validate: (v) =>
                      v === "" ||
                      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test((v || "").toUpperCase()) ||
                      "Format AAAAA9999A",
                  })}
                  placeholder="PAN (optional)"
                  className={`${baseInput} ${errors.pan ? errRing : okRing}`}
                />
              </Field>

              <Field label="DOB">
                <input
                  type="date"
                  {...register("dob")}
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              <Field label="Gender" required error={errors.gender}>
                <select
                  {...register("gender", { required: "Gender is required" })}
                  className={`${baseInput} ${errors.gender ? errRing : okRing}`}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
          )}

          {/* STEP 2 — ADDRESS */}
          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Address Line 1" span>
                <input
                  {...register("addressLine1")}
                  placeholder="Street / area"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              <Field label="City">
                <input
                  {...register("city")}
                  placeholder="City"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              <Field label="Pincode" error={errors.pincode}>
                <input
                  {...register("pincode", {
                    validate: (v) =>
                      v === "" || /^\d{6}$/.test(v) || "6 digits required",
                  })}
                  placeholder="6-digit pincode"
                  className={`${baseInput} ${
                    errors.pincode ? errRing : okRing
                  }`}
                />
              </Field>

              <Field label="State" required error={errors.state}>
                <select
                  {...register("state", { required: "State required" })}
                  className={`${baseInput} ${errors.state ? errRing : okRing}`}
                >
                 <option value="">Select State</option>

                  {indianStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}                </select>
              </Field>

              <Field label="Country" required error={errors.country}>
                <select
                  {...register("country", { required: "Country required" })}
                  className={`${baseInput} ${
                    errors.country ? errRing : okRing
                  }`}
                >
                  <option value="india">India</option>
                </select>
              </Field>
            </div>
          )}

          {/* STEP 3 — ADDITIONAL + LOGIN */}
          {step === 3 && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {/* Branches */}
              <SelectField
                label="Department"
                name="department"
                required
                data={departments?.data}
                field="departmentName"
                register={register}
                errors={errors.department}
              />

              {/* Role */}
              <SelectField
                label="Role"
                name="role"
                required
                data={roles?.data}
                field="roleName"
                register={register}
                errors={errors.role}
              />

              <Field label="Salary">
                <input
                  {...register("salary")}
                  placeholder="Salary (optional)"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>

              <Field
                label="Employee Commission"
                error={errors.employeeCommission}
              >
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  {...register("employeeCommission", {
                    validate: (v) => {
                      if (v === "" || v == null) return true;
                      const n = Number(v);
                      if (Number.isNaN(n)) return "Only number allowed";
                      if (n < 0) return "Minus value not allowed";
                      return true;
                    },
                  })}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e" || e.key === "E")
                      e.preventDefault();
                  }}
                  
                  placeholder="Commission (e.g. 1.5)"
                  className={`${baseInput} ${
                    errors.employeeCommission ? errRing : okRing
                  }`}
                />
              </Field>
              <div className="md:col-span-3">
                <Field label="Branches" required error={errors.branches}>
                  <Controller
                    name="branches"
                    control={control}
                    rules={{
                      validate: (v) =>
                        (Array.isArray(v) && v.length > 0) ||
                        "Branches are required",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti
                        options={allBranchOptions}
                        placeholder="Select branches"
                        closeMenuOnSelect={false}
                        classNamePrefix="rs"
                        menuPortalTarget={document.body}
                        styles={{
                          ...rsStyles,
                          control: (base, state) => ({
                            ...rsStyles.control(base, state),
                            borderColor: errors.branches
                              ? "#f87171"
                              : "#d1d5db",
                            ":hover": {
                              borderColor: errors.branches
                                ? "#ef4444"
                                : "#9481ce",
                            },
                          }),
                        }}
                        onChange={(val) => field.onChange(val)}
                        value={field.value}
                      />
                    )}
                  />
                </Field>
              </div>

              {/* Default Branch */}
              <div className="md:col-span-2">
                <Field label="Default Branch" required error={errors.branch}>
                  <Controller
                    name="branch"
                    control={control}
                    rules={{
                      validate: (v) => {
                        if (!selectedBranches || selectedBranches.length === 0)
                          return true;
                        return !!v?.value || "Default branch is required";
                      },
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isClearable
                        options={defaultBranchOptions}
                        placeholder={
                          (selectedBranches?.length || 0) > 0
                            ? "Select default branch"
                            : "Select branches first"
                        }
                        isDisabled={(selectedBranches?.length || 0) === 0}
                        classNamePrefix="rs"
                        menuPortalTarget={document.body}
                        styles={{
                          ...rsStyles,
                          control: (base, state) => ({
                            ...rsStyles.control(base, state),
                            borderColor: errors.branch ? "#f87171" : "#d1d5db",
                            ":hover": {
                              borderColor: errors.branch
                                ? "#ef4444"
                                : "#9481ce",
                            },
                          }),
                        }}
                        onChange={(val) => field.onChange(val)}
                        value={field.value}
                      />
                    )}
                  />
                </Field>
              </div>

              {/* ✅ Counters (Multi) — NOT mandatory */}
              <div className="md:col-span-3">
                <Field
                  label="Counters"
                  required={false}
                  error={errors.counters}
                >
                  <Controller
                    name="counters"
                    control={control}
                    rules={{
                      // ✅ optional: always valid
                      validate: () => true,
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti
                        options={counterOptions}
                        placeholder={
                          primaryBranchId
                            ? "Select counters (optional)"
                            : "Select branch first"
                        }
                        isDisabled={!primaryBranchId}
                        closeMenuOnSelect={false}
                        classNamePrefix="rs"
                        menuPortalTarget={document.body}
                        styles={{
                          ...rsStyles,
                          control: (base, state) => ({
                            ...rsStyles.control(base, state),
                            borderColor: errors.counters
                              ? "#f87171"
                              : "#d1d5db",
                            ":hover": {
                              borderColor: errors.counters
                                ? "#ef4444"
                                : "#9481ce",
                            },
                          }),
                        }}
                        onChange={(val) => {
                          field.onChange(val);

                          // ✅ if counters cleared -> clear default counter too
                          if (!val || val.length === 0) {
                            setValue("counter", null, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }
                        }}
                        value={field.value}
                      />
                    )}
                  />
                  {!primaryBranchId ? (
                    <p className="text-[11px] text-gray-500 mt-1">
                      (Default/selected branch choose karo to counters aayenge)
                    </p>
                  ) : null}
                </Field>
              </div>

              {/* ✅ Default Counter (Single) — mandatory ONLY IF counters selected */}
              <div className="md:col-span-2">
                <Field
                  label="Default Counter"
                  required={(selectedCounters?.length || 0) > 0}
                  error={errors.counter}
                >
                  <Controller
                    name="counter"
                    control={control}
                    rules={{
                      validate: (v) => {
                        const hasCounters = (selectedCounters?.length || 0) > 0;

                        // ✅ if no counters selected => default counter not required
                        if (!hasCounters) return true;

                        // ✅ counters selected => default counter required
                        if (!v?.value) return "Default counter is required";

                        // ✅ must be inside selected counters
                        const allowed = new Set(
                          (selectedCounters || []).map((x) => String(x?.value))
                        );
                        if (!allowed.has(String(v.value)))
                          return "Default counter must be one of selected counters";

                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isClearable
                        options={defaultCounterOptions}
                        placeholder={
                          (selectedCounters?.length || 0) > 0
                            ? "Select default counter"
                            : "Select counters first (optional)"
                        }
                        isDisabled={(selectedCounters?.length || 0) === 0}
                        classNamePrefix="rs"
                        menuPortalTarget={document.body}
                        styles={{
                          ...rsStyles,
                          control: (base, state) => ({
                            ...rsStyles.control(base, state),
                            borderColor: errors.counter ? "#f87171" : "#d1d5db",
                            ":hover": {
                              borderColor: errors.counter
                                ? "#ef4444"
                                : "#9481ce",
                            },
                          }),
                        }}
                        onChange={(val) => field.onChange(val)}
                        value={field.value}
                      />
                    )}
                  />
                </Field>
              </div>

              {/* Login section */}
              <div className="md:col-span-5 mt-2 border-t pt-3">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    {...register("createLogin")}
                    className="h-4 w-4 text-[#9481ce] focus:ring-[#9481ce]"
                  />
                  Create Login Access for this Employee
                </label>
              </div>

              {createLogin && (
                <>
                  {/* ✅ Prefix editable, suffix fixed */}
                  <div className="col-span-2">
                    <Field
                      label="Official Email"
                      required
                      error={errors.officialEmailPrefix}
                    >
                      <div className="flex">
                        <input
                          {...register("officialEmailPrefix", {
                            validate: (v) => {
                              if (!createLogin) return true;
                              const t = safe(v).trim();
                              if (!t) return "Email prefix required";
                              if (!/^[a-zA-Z0-9._-]{2,30}$/.test(t))
                                return "Use 2-30 chars: a-z, 0-9, . _ -";
                              return true;
                            },
                          })}
                          placeholder="employee name"
                          className={`${baseInput} rounded-r-none ${
                            errors.officialEmailPrefix ? errRing : okRing
                          }`}
                          onChange={(e) => {
                            const val = e.target.value;
                            setValue("officialEmailPrefix", val, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                        />

                        <span className="inline-flex items-center px-3 border border-l-0 rounded-r-lg bg-gray-100 text-gray-700 text-[14px]">
                          {officialEmailSuffix}
                        </span>
                      </div>

                      <p className="text-[11px] text-gray-500 mt-1">
                        Final Email:{" "}
                        <span className="font-semibold text-gray-700">
                          {(safe(officialEmailPrefix).trim() || "employee") +
                            officialEmailSuffix}
                        </span>
                      </p>
                    </Field>
                  </div>

                  <Field label="Password" required error={errors.password}>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        {...register("password", {
                          validate: (v) => {
                            if (!createLogin) return true;
                            if (!v) return "Password required";
                            if (String(v).length < 6) return "Min 6 chars";
                            return true;
                          },
                        })}
                        placeholder="Temporary password"
                        className={`${baseInput} pr-10 ${
                          errors.password ? errRing : okRing
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-[#9481ce]"
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <FaEyeSlash size={16} />
                        ) : (
                          <FaEye size={16} />
                        )}
                      </button>
                    </div>
                  </Field>
                </>
              )}
            </div>
          )}

          {/* STEP 4 — BANK DETAILS */}
          {step === 4 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Bank Name">
                <input
                  {...register("bankName")}
                  placeholder="Bank name"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>
              <Field label="Account Holder Name">
                <input
                  {...register("accountHolderName")}
                  placeholder="Holder name"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>
              <Field label="Account Number" error={errors.accountNumber}>
                <input
                  {...register("accountNumber", {
                    validate: (v) =>
                      v === "" ||
                      /^\d{9,18}$/.test(v) ||
                      "9–18 digits required",
                  })}
                  placeholder="Account number"
                  className={`${baseInput} ${
                    errors.accountNumber ? errRing : okRing
                  }`}
                />
              </Field>
              <Field label="Branch">
                <input
                  {...register("accountBranch")}
                  placeholder="Branch name"
                  className={`${baseInput} ${okRing}`}
                />
              </Field>
              <Field label="IFSC Code" error={errors.ifscCode}>
                <input
                  {...register("ifscCode", {
                    validate: (v) =>
                      v === "" ||
                      /^[A-Z]{4}0[A-Z0-9]{6}$/.test((v || "").toUpperCase()) ||
                      "Invalid IFSC",
                  })}
                  placeholder="IFSC e.g. SBIN0000456"
                  className={`${baseInput} ${
                    errors.ifscCode ? errRing : okRing
                  }`}
                />
              </Field>
            </div>
          )}

          {/* footer */}
          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={prevStep}
              className="inline-flex justify-center items-center px-4 py-2.5 rounded-lg font-medium border bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-60"
              disabled={step === 1 || loading}
            >
              Previous
            </motion.button>

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (step < 4) nextStep();
                else handleSubmit(onSubmit)();
              }}
              className="inline-flex justify-center items-center px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={loading}
            >
              {loading ? "Saving..." : step === 4 ? "Submit (Ctrl+S)" : "Next"}
            </motion.button>

            <Link
              to="/members/employee-list"
              className="inline-flex justify-center items-center px-4 py-2.5 rounded-lg font-medium border bg-white hover:bg-gray-50 text-gray-700"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

/* ---------- small reusable field components ---------- */
const Field = ({ label, required, error, children, span }) => (
  <div className={span ? "md:col-span-3" : ""}>
    <label className="block text-[13px] font-medium text-gray-700 ">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {/* {error ? (
      <p className="text-[11px] text-red-500 mt-1">
        {error?.message || "This field is required"}
      </p>
    ) : null} */}
  </div>
);

const SelectField = ({
  label,
  name,
  required,
  data,
  field,
  register,
  errors,
  disabled = false,
  helperText = "",
}) => (
  <Field label={label} required={required} error={errors}>
    <select
      {...register(name, required ? { required: `${label} is required` } : {})}
      disabled={disabled}
      className={`${baseInput} ${errors ? errRing : okRing} ${
        disabled ? "bg-gray-100 cursor-not-allowed" : ""
      }`}
    >
      <option value="">Select {label.toLowerCase()}</option>
      {data?.map((item) => (
        <option key={item._id} value={item._id}>
          {item[field]}
        </option>
      ))}
    </select>
    {helperText ? (
      <p className="text-[11px] text-gray-500 mt-1">{helperText}</p>
    ) : null}
  </Field>
);

export default CreateEmployee;
